import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Code2,
  Compass,
  Gauge,
  Layers3,
  MessageSquareText,
  MoveUpRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { marketingStats, portfolioFallback } from "@/data/marketing";

const reveal = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0 },
};

const capabilities = [
  {
    number: "01",
    icon: Compass,
    title: "Find the sharpest proposition",
    copy: "We turn a busy brief into a clear story, a useful structure and a reason for customers to choose you.",
  },
  {
    number: "02",
    icon: Layers3,
    title: "Design the complete experience",
    copy: "Responsive screens, states and a practical design system—considered from first impression to final action.",
  },
  {
    number: "03",
    icon: Code2,
    title: "Build it to last",
    copy: "Modern frontend and backend engineering, clean integrations, accessible interactions and maintainable code.",
  },
  {
    number: "04",
    icon: Gauge,
    title: "Launch, measure, improve",
    copy: "Performance, analytics and post-launch support are part of the engagement—not an afterthought.",
  },
];

export default function Landing() {
  const [stats, setStats] = useState(marketingStats);
  const [featured, setFeatured] = useState(portfolioFallback.slice(0, 3));

  useEffect(() => {
    api.get("/portfolio")
      .then(({ data }) => {
        if (Array.isArray(data) && data.length) setFeatured(data.slice(0, 3));
      })
      .catch(() => {});

    api.get("/stats")
      .then(({ data }) => {
        if (!data) return;
        setStats([
          { value: `${data.projects}+`, label: "projects delivered" },
          { value: `${data.clients}+`, label: "client partnerships" },
          { value: `${data.countries}`, label: "countries reached" },
          { value: `${data.satisfaction}%`, label: "client satisfaction" },
        ]);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="marketing-page" data-testid="landing-page">
      <section className="hero-shell relative overflow-hidden" aria-labelledby="hero-title">
        <div className="hero-orb hero-orb-one" aria-hidden="true" />
        <div className="hero-orb hero-orb-two" aria-hidden="true" />
        <div className="page-container relative grid lg:grid-cols-12 gap-12 lg:gap-8 items-center py-16 sm:py-20 lg:py-28">
          <motion.div
            initial="hidden"
            animate="visible"
            transition={{ staggerChildren: 0.1 }}
            className="lg:col-span-7"
          >
            <motion.div variants={reveal} className="studio-pill" data-testid="studio-positioning">
              <span aria-hidden="true" /> Senior-led strategy, design and engineering
            </motion.div>
            <motion.h1
              id="hero-title"
              variants={reveal}
              className="hero-title font-display"
            >
              Websites built to earn <em>attention</em>—and action.
            </motion.h1>
            <motion.p variants={reveal} className="hero-copy">
              Craftora brings strategy, product design and full-stack engineering into one senior-led team. You get a clearer story, a faster experience and a product your business can keep building on.
            </motion.p>
            <motion.div variants={reveal} className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/booking" className="pill-btn accent justify-center" data-testid="hero-book-btn">
                Book a discovery call <ArrowUpRight size={17} aria-hidden="true" />
              </Link>
              <Link to="/portfolio" className="pill-btn ghost justify-center" data-testid="hero-work-btn">
                See selected work <ArrowRight size={17} aria-hidden="true" />
              </Link>
            </motion.div>
            <motion.div variants={reveal} className="hero-proof" data-testid="hero-proof">
              {["Clear scope before build", "Weekly progress you can see", "No handoff between design and code"].map((item) => (
                <span key={item}><Check size={14} aria-hidden="true" /> {item}</span>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 28, rotate: 1 }}
            animate={{ opacity: 1, x: 0, rotate: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-5"
            data-testid="hero-project-preview"
          >
            <div className="project-preview">
              <div className="preview-toolbar">
                <div className="flex gap-1.5" aria-hidden="true"><i /><i /><i /></div>
                <span>PROJECT / 024</span>
                <span className="preview-live"><b /> IN BUILD</span>
              </div>
              <div className="preview-image">
                <img
                  src={portfolioFallback[1].cover}
                  alt="Commerce experience displayed on a laptop"
                  fetchPriority="high"
                />
                <div className="preview-note preview-note-top">
                  <span>Conversion path</span><strong>Reframed</strong>
                </div>
                <div className="preview-note preview-note-bottom">
                  <span>Performance target</span><strong>90+</strong>
                </div>
              </div>
              <div className="preview-footer">
                <div><span>DISCOVER</span><b>01</b></div>
                <ArrowRight size={15} aria-hidden="true" />
                <div><span>DESIGN</span><b>02</b></div>
                <ArrowRight size={15} aria-hidden="true" />
                <div><span>BUILD</span><b>03</b></div>
                <ArrowRight size={15} aria-hidden="true" />
                <div><span>GROW</span><b>04</b></div>
              </div>
            </div>
            <div className="project-preview-shadow" aria-hidden="true" />
          </motion.div>
        </div>

        <div className="border-y border-white/10">
          <div className="page-container grid grid-cols-2 lg:grid-cols-4">
            {stats.map((stat, index) => (
              <div className="stat-cell" key={`${stat.value}-${index}`} data-testid={`stat-${index}`}>
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="page-container section-space" aria-labelledby="work-title">
        <div className="section-heading-grid">
          <div>
            <p className="eyebrow">Selected work</p>
            <h2 id="work-title" className="section-title">The work should explain the value before we do.</h2>
          </div>
          <div className="section-intro">
            <p>Commercial design is not decoration. Each decision should make the offer easier to understand, trust and buy.</p>
            <Link to="/portfolio" className="text-link" data-testid="home-all-work-link">View all work <ArrowUpRight size={15} /></Link>
          </div>
        </div>

        <div className="work-grid mt-12">
          {featured.map((item, index) => (
            <Link
              key={item.item_id}
              to={`/portfolio/${item.item_id}`}
              className={`work-card work-card-${index + 1}`}
              data-testid={`featured-work-${item.item_id}`}
            >
              <div className="work-image-wrap">
                <img src={item.cover} alt="" loading={index ? "lazy" : "eager"} />
                <span className="work-arrow"><MoveUpRight size={20} /></span>
              </div>
              <div className="work-meta">
                <div><span>{item.category.replace(/-/g, " ")}</span><span>{item.industry || item.style}</span></div>
                <h3>{item.title}</h3>
                <p>{item.summary}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="capability-band" aria-labelledby="capabilities-title">
        <div className="page-container section-space">
          <div className="section-heading-grid">
            <div>
              <p className="eyebrow">One connected team</p>
              <h2 id="capabilities-title" className="section-title">From “we need a website” to a product real customers can use.</h2>
            </div>
            <p className="section-intro">Fewer handoffs means fewer lost decisions. The same small team stays close to your goals from the first conversation through release.</p>
          </div>
          <div className="capability-list mt-14">
            {capabilities.map(({ number, icon: Icon, title, copy }) => (
              <article className="capability-row" key={number} data-testid={`capability-${number}`}>
                <span className="cap-number">{number}</span>
                <Icon size={25} aria-hidden="true" />
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="page-container section-space" aria-labelledby="process-title">
        <div className="process-panel">
          <div className="process-copy">
            <p className="eyebrow">How the work moves</p>
            <h2 id="process-title" className="section-title">Calm process. Visible progress. No black box.</h2>
            <p>Every engagement has a clear owner, a shared workspace and a weekly decision rhythm. You always know what is happening, why it matters and what comes next.</p>
            <Link to="/services" className="pill-btn ghost mt-8" data-testid="home-process-services">Explore our services <ArrowRight size={16} /></Link>
          </div>
          <div className="process-steps">
            {[
              ["01", "Align", "Goals, audience, scope and success measures"],
              ["02", "Shape", "Narrative, journeys and interface direction"],
              ["03", "Build", "Production code, content and integrations"],
              ["04", "Release", "QA, measurement and post-launch support"],
            ].map(([n, title, copy]) => (
              <div className="process-step" key={n} data-testid={`process-${n}`}>
                <span>{n}</span><div><h3>{title}</h3><p>{copy}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="page-container pb-20 sm:pb-28" aria-labelledby="cta-title">
        <div className="commercial-cta">
          <div>
            <p className="eyebrow">Have a project in mind?</p>
            <h2 id="cta-title">Let’s make the next version the one that moves the business forward.</h2>
          </div>
          <div className="cta-side">
            <p>A focused 30-minute conversation is enough to understand the opportunity, risks and most useful next step.</p>
            <Link to="/booking" className="pill-btn ink" data-testid="home-final-cta">
              Start the conversation <MessageSquareText size={17} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
